import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Inbox,
  AlertCircle,
  AlertTriangle,
  ClipboardCheck,
  PlaneTakeoff,
  Users,
  Building2,
  HardDrive,
  User as UserIcon,
  UserPlus,
  FilePlus,
  UserCheck,
  Monitor,
} from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CreateRequestModal } from '@/components/requests/CreateRequestModal';
import { useStaffDashboard } from '@/api/dashboard';
import { useMe } from '@/api/users';
import { usePageTitle } from '@/hooks/usePageTitle';
import { apiError } from '@/lib/api';
import type { DashboardOverdueRequest } from '@/types/api';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function StaffDashboardPage() {
  usePageTitle('Dashboard');
  const { data, isLoading, isError, error } = useStaffDashboard();
  const me = useMe();
  const [showCreateRequest, setShowCreateRequest] = useState(false);

  const topOverdue = data?.top_overdue_requests ?? [];
  const overdueCount = data?.overdue_requests ?? 0;
  const pendingReviews = data?.pending_reviews ?? 0;
  const hasPriority = overdueCount > 0 || pendingReviews > 0 || topOverdue.length > 0;

  return (
    <AppShell crumbs={[{ label: 'Dashboard' }]}>
      {me.data?.is_ooo && (
        <div className="mb-4 flex items-center justify-between gap-3 bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-900">
          <div className="flex items-center gap-2">
            <PlaneTakeoff size={16} />
            <span>
              You're currently out of office. Remember to turn this off when
              you're back.
            </span>
          </div>
          <Link
            to="/profile"
            className="text-xs font-semibold text-yellow-900 underline hover:no-underline"
          >
            Manage
          </Link>
        </div>
      )}
      {/* Desktop app download banner */}
      <div className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-[#E3E3F1] bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EFF0FF]">
            <Monitor size={16} className="text-[#3F4CFF]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#12122C]">BillerBay Exchange — Desktop App</p>
            <p className="text-xs text-[#676687]">Sync files automatically from Windows Explorer.</p>
          </div>
        </div>
        <a
          href={`${import.meta.env.VITE_API_BASE_URL ?? '/api'}/downloads/desktop`}
          className="shrink-0"
        >
          <Button variant="secondary" className="text-sm">
            <svg width={14} height={14} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M3 3h8v8H3zm10 0h8v8h-8zM3 13h8v8H3zm10 0h8v8h-8z" />
            </svg>
            Download for Windows
          </Button>
        </a>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Link to="/admin/onboard">
          <Button variant="secondary" className="text-sm">
            <UserPlus size={16} /> Onboard client
          </Button>
        </Link>
        <Button
          variant="secondary"
          className="text-sm"
          onClick={() => setShowCreateRequest(true)}
        >
          <FilePlus size={16} /> <span className="hidden sm:inline">New request</span>
        </Button>
        <Link to="/admin/users">
          <Button variant="secondary" className="text-sm">
            <UserCheck size={16} /> Invite user
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner size={28} />
        </div>
      ) : isError ? (
        <Card>
          <div className="text-sm text-red-600">{apiError(error)}</div>
        </Card>
      ) : !data ? (
        <EmptyState title="No data" />
      ) : (
        <div className="space-y-6">
          {hasPriority && (
            <section className="space-y-3">
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-600">
                Priority
              </h2>

              {overdueCount > 0 && (
                <Link
                  to="/requests?status=overdue"
                  className="flex items-center justify-between gap-3 bg-red-50 border border-red-200 px-4 py-3 hover:bg-red-100"
                >
                  <div className="flex items-center gap-2 text-red-800">
                    <AlertTriangle size={18} />
                    <span className="text-sm font-semibold">
                      {overdueCount} overdue request{overdueCount === 1 ? '' : 's'}{' '}
                      across your clients
                    </span>
                  </div>
                  <span className="text-xs font-medium text-red-700">
                    View overdue →
                  </span>
                </Link>
              )}

              {pendingReviews > 0 && (
                <Link
                  to="/requests?status=submitted"
                  className="flex items-center justify-between gap-3 bg-yellow-50 border border-yellow-200 px-4 py-3 hover:bg-yellow-100"
                >
                  <div className="flex items-center gap-2 text-yellow-900">
                    <ClipboardCheck size={18} />
                    <span className="text-sm font-semibold">
                      {pendingReviews} submission{pendingReviews === 1 ? '' : 's'}{' '}
                      waiting for review
                    </span>
                  </div>
                  <span className="text-xs font-medium text-yellow-800">
                    View queue →
                  </span>
                </Link>
              )}

              {topOverdue.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {topOverdue.map((req) => (
                    <OverdueRequestCard key={req.id} req={req} />
                  ))}
                </div>
              )}
            </section>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Open requests" value={data.open_requests} icon={<Inbox size={18} />} />
            <StatCard
              label="Overdue"
              value={data.overdue_requests}
              icon={<AlertCircle size={18} />}
              accent="text-red-600"
            />
            <StatCard
              label="Pending review"
              value={data.pending_reviews}
              icon={<ClipboardCheck size={18} />}
            />
            <StatCard label="Active clients" value={data.active_clients} icon={<Users size={18} />} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Storage summary */}
            <Card title="Storage usage">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-md bg-[#EFF0FF]">
                  <HardDrive size={18} className="text-[#0410BD]" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#12122C]">
                    {formatBytes(data.total_storage_bytes ?? 0)}
                  </div>
                  <div className="text-xs text-[#676687]">total across all clients</div>
                </div>
              </div>
              {(data.org_storage ?? []).length > 0 && (() => {
                const total = data.total_storage_bytes ?? 1;
                return (
                  <ul className="space-y-3">
                    {(data.org_storage ?? []).map((org) => {
                      const pct = total > 0 ? Math.min(100, (org.used_bytes / total) * 100) : 0;
                      return (
                        <li key={org.id}>
                          <div className="flex items-center justify-between gap-2 text-sm mb-1">
                            <Link to={`/admin/organizations/${org.id}`} className="font-medium text-[#12122C] hover:text-[#0410BD] truncate flex-1">
                              {org.name}
                            </Link>
                            <span className="text-[#676687] shrink-0">{formatBytes(org.used_bytes)}</span>
                          </div>
                          <div className="h-1.5 bg-[#EFF0FF] rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#0410BD] rounded-full transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
              {(data.org_storage ?? []).length === 0 && (
                <p className="text-sm text-slate-400">No files stored yet.</p>
              )}
            </Card>

            {/* Compact recent activity */}
            <Card
              title="Recent activity"
              actions={
                <Link to="/admin/audit" className="text-xs text-[#0410BD] hover:underline">
                  View all →
                </Link>
              }
            >
              {data.recent_activity.length === 0 ? (
                <EmptyState
                  title="No activity"
                  description="Activity from clients you're assigned to will appear here."
                />
              ) : (
                <ul className="divide-y divide-slate-100">
                  {data.recent_activity.slice(0, 8).map((log) => (
                    <li key={log.id} className="py-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm text-slate-800 min-w-0">
                          <span className="font-medium">{log.actor_name ?? 'Someone'}</span>{' '}
                          <span className="text-slate-500">{log.action.replace(/_/g, ' ')}</span>
                        </span>
                        <span className="text-xs text-slate-400 whitespace-nowrap shrink-0">
                          {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      )}

      <CreateRequestModal
        open={showCreateRequest}
        onClose={() => setShowCreateRequest(false)}
      />
    </AppShell>
  );
}

function OverdueRequestCard({ req }: { req: DashboardOverdueRequest }) {
  const dueDate = req.due_date ? new Date(req.due_date) : null;
  const responderLabel = req.responder_name ?? 'Unassigned';
  return (
    <Link
      to={`/requests/${req.id}`}
      className="block bg-white border border-red-200 p-4 hover:border-red-400 hover:shadow-sm transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800 truncate">{req.title}</div>
        <Badge tone="red">
          <AlertTriangle size={12} /> Overdue
        </Badge>
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
        <Building2 size={12} />
        <span className="truncate">{req.organization_name}</span>
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs text-slate-500">
        <UserIcon size={12} />
        <span className="truncate">{responderLabel}</span>
      </div>
      {dueDate && (
        <div className="mt-1 text-xs text-red-700">
          Was due {formatDistanceToNow(dueDate, { addSuffix: true })} ·{' '}
          {format(dueDate, 'PP')}
        </div>
      )}
    </Link>
  );
}

function StatCard({
  label,
  value,
  icon,
  accent,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  accent?: string;
}) {
  return (
    <div className="bg-white border border-[#E3E3F1] rounded-lg shadow-[0_1px_2px_rgba(15,23,42,0.06)] p-4">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#676687]">{label}</span>
        <span className={`p-2 rounded-md bg-[#EFF0FF] text-[#0410BD] ${accent ?? ''}`}>{icon}</span>
      </div>
      <div className={`text-3xl font-bold mt-2 tabular-nums ${accent ?? 'text-[#12122C]'}`}>{value}</div>
    </div>
  );
}
